import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../App';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { ScrollArea } from './ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Checkbox } from './ui/checkbox';
import { 
  Package, 
  Plus, 
  GripVertical, 
  Link2, 
  Trash2, 
  Edit,
  ChevronRight,
  AlertCircle,
  CheckCircle2,
  Clock,
  Lock,
  Pin,
  ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TaskBundles = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [bundles, setBundles] = useState([]);
  const [users, setUsers] = useState([]);
  const [availableTasks, setAvailableTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedBundle, setSelectedBundle] = useState(null);
  
  // Form state
  const [bundleName, setBundleName] = useState('');
  const [bundleDescription, setBundleDescription] = useState('');
  const [assignedTo, setAssignedTo] = useState('');
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [dependencies, setDependencies] = useState({}); // {task_id: [depends_on_task_ids]}

  const fetchBundles = useCallback(async () => {
    try {
      const response = await fetch(`${API}/bundles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setBundles(data);
      }
    } catch (error) {
      console.error('Error fetching bundles:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await fetch(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  }, [token]);

  const fetchAvailableTasks = useCallback(async () => {
    try {
      const response = await fetch(`${API}/tasks/available-for-bundle`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAvailableTasks(data);
      }
    } catch (error) {
      console.error('Error fetching available tasks:', error);
    }
  }, [token]);

  useEffect(() => {
    fetchBundles();
    fetchUsers();
    fetchAvailableTasks();
  }, [fetchBundles, fetchUsers, fetchAvailableTasks]);

  const resetForm = () => {
    setBundleName('');
    setBundleDescription('');
    setAssignedTo('');
    setSelectedTasks([]);
    setDependencies({});
  };

  const openCreateModal = () => {
    resetForm();
    fetchAvailableTasks();
    setShowCreateModal(true);
  };

  const openEditModal = (bundle) => {
    setSelectedBundle(bundle);
    setBundleName(bundle.name);
    setBundleDescription(bundle.description);
    setAssignedTo(bundle.assigned_to);
    
    // Set selected tasks with their order
    const tasks = bundle.tasks.map(t => ({
      task_id: t.task_id,
      task_name: t.task_name,
      task_status: t.task_status,
      order: t.order
    }));
    setSelectedTasks(tasks);
    
    // Set dependencies
    const deps = {};
    bundle.tasks.forEach(t => {
      if (t.depends_on && t.depends_on.length > 0) {
        deps[t.task_id] = t.depends_on;
      }
    });
    setDependencies(deps);
    
    fetchAvailableTasks();
    setShowEditModal(true);
  };

  const handleTaskSelect = (task, isChecked) => {
    if (isChecked) {
      setSelectedTasks(prev => [...prev, {
        task_id: task.id,
        task_name: task.name,
        task_status: task.status,
        order: prev.length
      }]);
    } else {
      setSelectedTasks(prev => prev.filter(t => t.task_id !== task.id));
      // Remove from dependencies
      setDependencies(prev => {
        const newDeps = { ...prev };
        delete newDeps[task.id];
        // Remove from other tasks' dependencies
        Object.keys(newDeps).forEach(key => {
          newDeps[key] = newDeps[key].filter(d => d !== task.id);
        });
        return newDeps;
      });
    }
  };

  const moveTask = (index, direction) => {
    const newTasks = [...selectedTasks];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= newTasks.length) return;
    
    [newTasks[index], newTasks[newIndex]] = [newTasks[newIndex], newTasks[index]];
    // Update order values
    newTasks.forEach((t, i) => t.order = i);
    setSelectedTasks(newTasks);
  };

  const toggleDependency = (taskId, dependsOnId) => {
    setDependencies(prev => {
      const current = prev[taskId] || [];
      if (current.includes(dependsOnId)) {
        return { ...prev, [taskId]: current.filter(d => d !== dependsOnId) };
      } else {
        return { ...prev, [taskId]: [...current, dependsOnId] };
      }
    });
  };

  const handleCreateBundle = async () => {
    if (!bundleName.trim()) {
      toast.error('Please enter a bundle name');
      return;
    }
    if (!assignedTo) {
      toast.error('Please select an assignee');
      return;
    }
    if (selectedTasks.length === 0) {
      toast.error('Please add at least one task to the bundle');
      return;
    }

    try {
      const tasks = selectedTasks.map((t, index) => ({
        task_id: t.task_id,
        order: index,
        depends_on: dependencies[t.task_id] || []
      }));

      const response = await fetch(`${API}/bundles`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          name: bundleName,
          description: bundleDescription,
          assigned_to: assignedTo,
          tasks: tasks
        })
      });

      if (response.ok) {
        toast.success('Bundle created successfully!');
        setShowCreateModal(false);
        resetForm();
        fetchBundles();
        fetchAvailableTasks();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create bundle');
      }
    } catch (error) {
      toast.error('Failed to create bundle');
    }
  };

  const handleUpdateBundle = async () => {
    if (!bundleName.trim()) {
      toast.error('Please enter a bundle name');
      return;
    }

    try {
      const tasks = selectedTasks.map((t, index) => ({
        task_id: t.task_id,
        order: index,
        depends_on: dependencies[t.task_id] || []
      }));

      const response = await fetch(`${API}/bundles/${selectedBundle.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          name: bundleName,
          description: bundleDescription,
          assigned_to: assignedTo,
          tasks: tasks
        })
      });

      if (response.ok) {
        toast.success('Bundle updated successfully!');
        setShowEditModal(false);
        resetForm();
        fetchBundles();
        fetchAvailableTasks();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update bundle');
      }
    } catch (error) {
      toast.error('Failed to update bundle');
    }
  };

  const handleDeleteBundle = async (bundleId) => {
    if (!window.confirm('Are you sure you want to delete this bundle? Tasks will not be deleted.')) {
      return;
    }

    try {
      const response = await fetch(`${API}/bundles/${bundleId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Bundle deleted successfully!');
        fetchBundles();
        fetchAvailableTasks();
      } else {
        toast.error('Failed to delete bundle');
      }
    } catch (error) {
      toast.error('Failed to delete bundle');
    }
  };

  const handleTogglePin = async (bundleId, currentPinState) => {
    try {
      const response = await fetch(`${API}/bundles/${bundleId}/pin`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success(currentPinState ? 'Bundle unpinned from dashboard' : 'Bundle pinned to dashboard');
        fetchBundles();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to toggle pin');
      }
    } catch (error) {
      toast.error('Failed to toggle pin');
    }
  };

  const navigateToTask = (taskId, e) => {
    e.stopPropagation();
    navigate(`/tasks/${taskId}`);
  };

  const getStatusIcon = (status, isBlocked) => {
    if (isBlocked) {
      return <Lock className="w-4 h-4 text-orange-500" />;
    }
    switch (status) {
      case 'completed':
        return <CheckCircle2 className="w-4 h-4 text-green-500" />;
      case 'in progress':
        return <Clock className="w-4 h-4 text-blue-500" />;
      default:
        return <AlertCircle className="w-4 h-4 text-muted-foreground" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-500/10 text-green-600 dark:text-green-400';
      case 'in progress':
        return 'bg-blue-500/10 text-blue-600 dark:text-blue-400';
      default:
        return 'bg-gray-500/10 text-gray-600 dark:text-gray-400';
    }
  };

  // Get all tasks (available + already in bundle for edit mode)
  const getAllTasksForSelection = () => {
    if (showEditModal && selectedBundle) {
      // In edit mode, include both available tasks and tasks already in the bundle
      const bundleTaskIds = selectedBundle.tasks.map(t => t.task_id);
      const bundleTasks = selectedBundle.tasks.map(t => ({
        id: t.task_id,
        name: t.task_name,
        status: t.task_status,
        description: t.task_description
      }));
      const filteredAvailable = availableTasks.filter(t => !bundleTaskIds.includes(t.id));
      return [...bundleTasks, ...filteredAvailable];
    }
    return availableTasks;
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="font-heading text-lg flex items-center gap-2">
          <Package className="w-5 h-5" />
          Task Bundles
        </CardTitle>
        <Button size="sm" onClick={openCreateModal} data-testid="create-bundle-btn">
          <Plus className="w-4 h-4 mr-1" />
          New Bundle
        </Button>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-center py-8 text-muted-foreground">Loading bundles...</div>
        ) : bundles.length === 0 ? (
          <div className="text-center py-8">
            <Package className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
            <p className="text-muted-foreground">No task bundles yet</p>
            <p className="text-sm text-muted-foreground mt-1">Create a bundle to group related tasks together</p>
          </div>
        ) : (
          <div className="space-y-4">
            {bundles.map((bundle) => (
              <div
                key={bundle.id}
                className="p-4 rounded-xl border bg-card transition-all duration-200 hover:bg-muted/50 hover:shadow-md"
                data-testid={`bundle-card-${bundle.id}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h4 className="font-medium">{bundle.name}</h4>
                    {bundle.description && (
                      <p className="text-sm text-muted-foreground mt-1">{bundle.description}</p>
                    )}
                    <p className="text-xs text-muted-foreground mt-1">
                      Assigned to: {bundle.assigned_to_name || 'Unassigned'}
                    </p>
                  </div>
                  <div className="flex gap-1">
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className={`h-8 w-8 transition-colors ${bundle.is_pinned ? 'text-primary bg-primary/10' : 'text-muted-foreground hover:text-primary'}`}
                      onClick={() => handleTogglePin(bundle.id, bundle.is_pinned)}
                      title={bundle.is_pinned ? 'Unpin from dashboard' : 'Pin to dashboard'}
                      data-testid={`pin-bundle-${bundle.id}`}
                    >
                      <Pin className={`w-4 h-4 ${bundle.is_pinned ? 'fill-current' : ''}`} />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-8 w-8"
                      onClick={() => openEditModal(bundle)}
                      data-testid={`edit-bundle-${bundle.id}`}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-8 w-8 text-destructive hover:text-destructive"
                      onClick={() => handleDeleteBundle(bundle.id)}
                      data-testid={`delete-bundle-${bundle.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                
                {/* Progress Bar */}
                <div className="mb-3">
                  <div className="flex justify-between text-xs text-muted-foreground mb-1">
                    <span>{bundle.completed_tasks} of {bundle.total_tasks} tasks completed</span>
                    <span>{bundle.progress_percent}%</span>
                  </div>
                  <Progress value={bundle.progress_percent} className="h-2" />
                </div>
                
                {/* Task List Preview - Clickable */}
                <div className="space-y-2">
                  {bundle.tasks.slice(0, 3).map((task, idx) => (
                    <div 
                      key={task.task_id}
                      onClick={(e) => navigateToTask(task.task_id, e)}
                      className={`group flex flex-col gap-1 text-sm p-2 rounded-lg cursor-pointer transition-all ${
                        task.is_blocked ? 'bg-orange-500/10 hover:bg-orange-500/20' : 'bg-muted/50 hover:bg-muted'
                      }`}
                      data-testid={`bundle-task-${task.task_id}`}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-muted-foreground w-5">{idx + 1}.</span>
                        {getStatusIcon(task.task_status, task.is_blocked)}
                        <span className={`flex-1 ${task.task_status === 'completed' ? 'line-through text-muted-foreground' : ''}`}>
                          {task.task_name}
                        </span>
                        {task.is_blocked && (
                          <Badge variant="outline" className="text-xs bg-orange-500/10 text-orange-600">
                            Blocked
                          </Badge>
                        )}
                        {task.depends_on.length > 0 && !task.is_blocked && (
                          <Link2 className="w-3 h-3 text-muted-foreground" title="Has dependencies" />
                        )}
                        <ChevronRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                      {/* Subtask Progress */}
                      {task.subtask_total > 0 && (
                        <div className="flex items-center gap-2 ml-7 text-xs text-muted-foreground">
                          <span>Sub-tasks: {task.subtask_completed}/{task.subtask_total}</span>
                          <div className="flex-1 max-w-24 h-1.5 bg-secondary/50 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-primary transition-all duration-500"
                              style={{ width: `${task.subtask_progress}%` }}
                            />
                          </div>
                          <span>{task.subtask_progress}%</span>
                        </div>
                      )}
                    </div>
                  ))}
                  {bundle.tasks.length > 3 && (
                    <p className="text-xs text-muted-foreground text-center py-1">
                      More... ({bundle.tasks.length - 3})
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      {/* Create/Edit Bundle Modal */}
      <Dialog open={showCreateModal || showEditModal} onOpenChange={(open) => {
        if (!open) {
          setShowCreateModal(false);
          setShowEditModal(false);
          resetForm();
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>
              {showEditModal ? 'Edit Task Bundle' : 'Create Task Bundle'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto space-y-4 py-4">
            {/* Bundle Name */}
            <div className="space-y-2">
              <Label>Bundle Name *</Label>
              <Input
                value={bundleName}
                onChange={(e) => setBundleName(e.target.value)}
                placeholder="e.g., Q1 Marketing Campaign"
                data-testid="bundle-name-input"
              />
            </div>
            
            {/* Description */}
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={bundleDescription}
                onChange={(e) => setBundleDescription(e.target.value)}
                placeholder="Describe the bundle purpose..."
                rows={2}
              />
            </div>
            
            {/* Assignee */}
            <div className="space-y-2">
              <Label>Assign To *</Label>
              <Select value={assignedTo} onValueChange={setAssignedTo}>
                <SelectTrigger data-testid="bundle-assignee-select">
                  <SelectValue placeholder="Select assignee" />
                </SelectTrigger>
                <SelectContent>
                  {users.map(user => (
                    <SelectItem key={user.id} value={user.id}>
                      {user.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Task Selection */}
            <div className="space-y-2">
              <Label>Select Tasks</Label>
              <div className="border rounded-lg p-3 max-h-40 overflow-y-auto">
                {getAllTasksForSelection().length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-2">
                    No available tasks. Create tasks first.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {getAllTasksForSelection().map(task => (
                      <div key={task.id} className="flex items-center gap-2">
                        <Checkbox
                          id={`task-${task.id}`}
                          checked={selectedTasks.some(t => t.task_id === task.id)}
                          onCheckedChange={(checked) => handleTaskSelect(task, checked)}
                        />
                        <label 
                          htmlFor={`task-${task.id}`}
                          className="text-sm flex-1 cursor-pointer"
                        >
                          {task.name}
                        </label>
                        <Badge className={getStatusColor(task.status)} variant="secondary">
                          {task.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            
            {/* Selected Tasks with Ordering and Dependencies */}
            {selectedTasks.length > 0 && (
              <div className="space-y-2">
                <Label>Task Order & Dependencies</Label>
                <p className="text-xs text-muted-foreground">
                  Drag to reorder. Click the link icon to set which tasks must be completed first.
                </p>
                <div className="border rounded-lg p-3 space-y-2">
                  {selectedTasks.map((task, index) => (
                    <div 
                      key={task.task_id}
                      className="flex items-center gap-2 p-2 rounded-lg bg-muted/50"
                    >
                      <div className="flex flex-col gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-5 w-5"
                          onClick={() => moveTask(index, 'up')}
                          disabled={index === 0}
                        >
                          <ChevronRight className="w-3 h-3 -rotate-90" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-5 w-5"
                          onClick={() => moveTask(index, 'down')}
                          disabled={index === selectedTasks.length - 1}
                        >
                          <ChevronRight className="w-3 h-3 rotate-90" />
                        </Button>
                      </div>
                      
                      <GripVertical className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm font-medium w-5">{index + 1}.</span>
                      <span className="text-sm flex-1">{task.task_name}</span>
                      
                      {/* Dependency Selection */}
                      {index > 0 && (
                        <Select
                          value={(dependencies[task.task_id] || [])[0] || 'none'}
                          onValueChange={(value) => {
                            if (value === 'none') {
                              setDependencies(prev => ({ ...prev, [task.task_id]: [] }));
                            } else {
                              setDependencies(prev => ({ ...prev, [task.task_id]: [value] }));
                            }
                          }}
                        >
                          <SelectTrigger className="w-40 h-8">
                            <div className="flex items-center gap-1">
                              <Link2 className="w-3 h-3" />
                              <SelectValue placeholder="Depends on..." />
                            </div>
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">No dependency</SelectItem>
                            {selectedTasks.slice(0, index).map(t => (
                              <SelectItem key={t.task_id} value={t.task_id}>
                                {t.task_name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                      
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive hover:text-destructive"
                        onClick={() => handleTaskSelect({ id: task.task_id }, false)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowCreateModal(false);
              setShowEditModal(false);
              resetForm();
            }}>
              Cancel
            </Button>
            <Button 
              onClick={showEditModal ? handleUpdateBundle : handleCreateBundle}
              data-testid="save-bundle-btn"
            >
              {showEditModal ? 'Update Bundle' : 'Create Bundle'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default TaskBundles;
